import axios from 'axios';
import API_BASE from '../apiBase';

// Create Axios instance with custom config
export const apiClient = axios.create({
    baseURL: API_BASE,
    timeout: 300000, // 5 minutes timeout for large file uploads
    headers: {
        'Content-Type': 'application/json',
    },
    maxContentLength: Infinity, // Allow unlimited upload size
    maxBodyLength: Infinity, // Allow unlimited request body size
});

// Add request interceptor for JWT authentication and debugging
apiClient.interceptors.request.use(
    (config) => {
        // Get access token from localStorage
        const accessToken = localStorage.getItem('floos_access_token');
        
        // Add JWT Authorization header to all requests (except auth endpoints)
        if (accessToken && !config.url?.includes('/api/auth/login')) {
            config.headers.Authorization = `Bearer ${accessToken}`;
        }

        return config;
    },
    (error) => {
        console.error('[API] Request error:', error);
        return Promise.reject(error);
    }
);

// Add response interceptor for debugging and token refresh
apiClient.interceptors.response.use(
    (response) => {
        // Log the successful response
        return response;
    },
    async (error) => {
        const originalRequest = error.config;
        
        // Log the error response
        if (error.response) {
            // Server responded with error status
            console.error(
                '[API] Error response:',
                error.response.status,
                error.response.data,
                'from:',
                error.config.url
            );
            
            // Handle 401 errors with token refresh
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                
                const refreshToken = localStorage.getItem('floos_refresh_token');
                
                if (refreshToken && !originalRequest.url?.includes('/api/auth/')) {
                    try {
                        
                        const refreshResponse = await axios.post(
                            `${API_BASE}/api/auth/refresh/`,
                            { refresh: refreshToken }
                        );
                        
                        if (refreshResponse.data.success && refreshResponse.data.access_token) {
                            const newAccessToken = refreshResponse.data.access_token;
                            localStorage.setItem('floos_access_token', newAccessToken);
                            
                            // Retry the original request with new token
                            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                            return apiClient(originalRequest);
                        }
                    } catch (refreshError) {
                        console.error('[API] Token refresh failed:', refreshError);
                        // Clear tokens and redirect to login
                        localStorage.removeItem('floos_access_token');
                        localStorage.removeItem('floos_refresh_token');
                        localStorage.removeItem('floos_user');
                        window.location.replace('/login');
                        return Promise.reject(refreshError);
                    }
                }
                
                // If no refresh token or refresh failed, redirect to login
                console.error('[API] Authentication failed: No valid refresh token');
                localStorage.removeItem('floos_access_token');
                localStorage.removeItem('floos_refresh_token');
                localStorage.removeItem('floos_user');
                window.location.replace('/login');
                error.message = 'Authentication failed: Please log in again';
            } else if (error.response.status === 403) {
                console.error('[API] Access forbidden: Insufficient permissions');
                error.message = 'Access forbidden: You do not have permission to access this resource';
            } else if (error.response.status >= 500) {
                console.error('[API] Server error:', error.response.status, error.response.data);
                error.message = 'Server error: Please try again later';
            }
        } else if (error.request) {
            // Request made but no response
            if (error.code === 'ECONNABORTED') {
                console.error(
                    '[API] Request timeout:',
                    error.config.url,
                    'Timeout:',
                    error.config.timeout,
                    'ms'
                );
            } else {
                console.error('[API] No response received:', error.request);
                error.message = 'Network error: Unable to connect to server. Please check your internet connection.';
            }
        } else {
            // Error in request configuration
            console.error('[API] Request configuration error:', error.message);
        }
        return Promise.reject(error);
    }
); 